/**
 * Authentication Modal Component
 * 
 * Modal for login/signup when guest tries to vote.
 * Supports:
 * - Email/password login
 * - Email/password signup
 * - Google OAuth login
 */
import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const AuthModal = ({ 
    isOpen, 
    onClose, 
    onLoginSuccess,
    enableGoogleLogin,
    enableEmailSignup,
    loginUrl,
    registerUrl,
    ajaxUrl
}) => {
    const [mode, setMode] = useState('login'); // 'login' or 'signup'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [isClosing, setIsClosing] = useState(false);
    const [showLoginPw, setShowLoginPw] = useState(false);
    const [showSignupPw, setShowSignupPw] = useState(false);
    const [showConfirmPw, setShowConfirmPw] = useState(false);

    if (!isOpen) return null;

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            setError('');
            onClose();
        }, 200);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await fetch(ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'cha_ajax_login',
                    username: formData.username,
                    password: formData.password,
                    security: window.chaFeedbackFrontend?.nonce || ''
                })
            });

            const data = await response.json();

            if (data.success) {
                if (onLoginSuccess) onLoginSuccess(data.data);
                handleClose();
                // Reload page to get fresh user state
                window.location.reload();
            } else {
                setError(data.data?.message || __( 'Login failed. Please check your credentials.', 'changeloger' ));
            }
        } catch (err) {
            setError(__( 'An error occurred. Please try again.', 'changeloger' ));
        } finally {
            setLoading(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError(__( 'Passwords do not match.', 'changeloger' ));
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'cha_ajax_register',
                    username: formData.username,
                    email: formData.email,
                    password: formData.password,
                    security: window.chaFeedbackFrontend?.nonce || ''
                })
            });

            const data = await response.json();

            if (data.success) {
                if (onLoginSuccess) onLoginSuccess(data.data);
                handleClose();
                window.location.reload();
            } else {
                setError(data.data?.message || __( 'Registration failed. Please try again.', 'changeloger' ));
            }
        } catch (err) {
            setError(__( 'An error occurred. Please try again.', 'changeloger' ));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = () => {
        // Redirect to Google OAuth start endpoint, asking it to bring us back here on success.
        const returnTo = window.location.href;
        const redirectUrl = `${window.location.origin}/wp-json/changeloger/v1/auth/google?return_to=${encodeURIComponent(returnTo)}`;
        window.location.href = redirectUrl;
    };

    return (
        <div className={`cha-modal-overlay active ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
            <div 
                className={`cha-auth-modal ${isClosing ? 'closing' : ''}`} 
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="cha-auth-header">
                    <div className="cha-auth-tabs">
                        <button 
                            className={`cha-auth-tab ${mode === 'login' ? 'active' : ''}`}
                            onClick={() => { setMode('login'); setError(''); }}
                        >
                            {__( 'Log In', 'changeloger' )}
                        </button>
                        {enableEmailSignup && (
                            <button 
                                className={`cha-auth-tab ${mode === 'signup' ? 'active' : ''}`}
                                onClick={() => { setMode('signup'); setError(''); }}
                            >
                                {__( 'Sign Up', 'changeloger' )}
                            </button>
                        )}
                    </div>
                    <button className="cha-auth-close" onClick={handleClose}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="cha-auth-body">
                    {/* Google Login Button */}
                    {enableGoogleLogin && (
                        <>
                            <button 
                                className="cha-google-btn"
                                onClick={handleGoogleLogin}
                                type="button"
                            >
                                <svg viewBox="0 0 24 24" width="20" height="20">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                {__( 'Continue with Google', 'changeloger' )}
                            </button>
                            <div className="cha-auth-divider">
                                <span>{__( 'or', 'changeloger' )}</span>
                            </div>
                        </>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="cha-auth-error">
                            {error}
                        </div>
                    )}

                    {/* Login Form */}
                    {mode === 'login' && (
                        <form onSubmit={handleLogin} className="cha-auth-form">
                            <div className="cha-auth-field">
                                <label htmlFor="cha-login-username">{__( 'Username or Email', 'changeloger' )}</label>
                                <input
                                    id="cha-login-username"
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={__( 'Enter your username or email', 'changeloger' )}
                                />
                            </div>
                            <div className="cha-auth-field">
                                <label htmlFor="cha-login-password">{__( 'Password', 'changeloger' )}</label>
                                <div className="cha-auth-password-wrap">
                                    <input
                                        id="cha-login-password"
                                        type={showLoginPw ? 'text' : 'password'}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required
                                        placeholder={__( 'Enter your password', 'changeloger' )}
                                    />
                                    <button
                                        type="button"
                                        className="cha-auth-eye-toggle"
                                        onClick={() => setShowLoginPw(!showLoginPw)}
                                        aria-label={showLoginPw ? __( 'Hide password', 'changeloger' ) : __( 'Show password', 'changeloger' )}
                                    >
                                        {showLoginPw ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 128 128" stroke="currentColor" width="18" height="18">
                                              <g transform="matrix(1,0,0,1,0,0)"><path opacity="1" fill='#475569' d="m79.891 65.078 7.27-7.27C87.69 59.787 88 61.856 88 64c0 13.234-10.766 24-24 24-2.144 0-4.213-.31-6.192-.839l7.27-7.27a15.929 15.929 0 0 0 14.813-14.813zm47.605-3.021c-.492-.885-7.47-13.112-21.11-23.474l-5.821 5.821c9.946 7.313 16.248 15.842 18.729 19.602C114.553 71.225 95.955 96 64 96c-4.792 0-9.248-.613-13.441-1.591l-6.573 6.573C50.029 102.835 56.671 104 64 104c41.873 0 62.633-36.504 63.496-38.057a3.997 3.997 0 0 0 0-3.886zm-16.668-39.229-88 88C22.047 111.609 21.023 112 20 112s-2.047-.391-2.828-1.172a3.997 3.997 0 0 1 0-5.656l11.196-11.196C10.268 83.049 1.071 66.964.504 65.943a4 4 0 0 1 0-3.887C1.367 60.504 22.127 24 64 24c10.827 0 20.205 2.47 28.222 6.122l12.95-12.95c1.563-1.563 4.094-1.563 5.656 0s1.563 4.094 0 5.656zM34.333 88.011 44.46 77.884C41.663 73.96 40 69.175 40 64c0-13.234 10.766-24 24-24 5.175 0 9.96 1.663 13.884 4.459l8.189-8.189C79.603 33.679 72.251 32 64 32 32.045 32 13.447 56.775 8.707 63.994c3.01 4.562 11.662 16.11 25.626 24.017zm15.934-15.935 21.809-21.809C69.697 48.862 66.958 48 64 48c-8.822 0-16 7.178-16 16 0 2.958.862 5.697 2.267 8.076z"></path></g>
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <button 
                                type="submit" 
                                className="cha-auth-submit"
                                disabled={loading}
                            >
                                {loading ? __( 'Logging in...', 'changeloger' ) : __( 'Log In', 'changeloger' )}
                            </button>
                        </form>
                    )}

                    {/* Signup Form */}
                    {mode === 'signup' && enableEmailSignup && (
                        <form onSubmit={handleSignup} className="cha-auth-form">
                            <div className="cha-auth-field">
                                <label htmlFor="cha-signup-username">{__( 'Username', 'changeloger' )}</label>
                                <input
                                    id="cha-signup-username"
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={__( 'Choose a username', 'changeloger' )}
                                />
                            </div>
                            <div className="cha-auth-field">
                                <label htmlFor="cha-signup-email">{__( 'Email', 'changeloger' )}</label>
                                <input
                                    id="cha-signup-email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={__( 'Enter your email', 'changeloger' )}
                                />
                            </div>
                            <div className="cha-auth-field">
                                <label htmlFor="cha-signup-password">{__( 'Password', 'changeloger' )}</label>
                                <div className="cha-auth-password-wrap">
                                    <input
                                        id="cha-signup-password"
                                        type={showSignupPw ? 'text' : 'password'}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required
                                        placeholder={__( 'Create a password', 'changeloger' )}
                                    />
                                    <button
                                        type="button"
                                        className="cha-auth-eye-toggle"
                                        onClick={() => setShowSignupPw(!showSignupPw)}
                                        aria-label={showSignupPw ? __( 'Hide password', 'changeloger' ) : __( 'Show password', 'changeloger' )}
                                    >
                                        {showSignupPw ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 128 128" stroke="currentColor" width="18" height="18">
                                              <g transform="matrix(1,0,0,1,0,0)"><path opacity="1" fill='#475569' d="m79.891 65.078 7.27-7.27C87.69 59.787 88 61.856 88 64c0 13.234-10.766 24-24 24-2.144 0-4.213-.31-6.192-.839l7.27-7.27a15.929 15.929 0 0 0 14.813-14.813zm47.605-3.021c-.492-.885-7.47-13.112-21.11-23.474l-5.821 5.821c9.946 7.313 16.248 15.842 18.729 19.602C114.553 71.225 95.955 96 64 96c-4.792 0-9.248-.613-13.441-1.591l-6.573 6.573C50.029 102.835 56.671 104 64 104c41.873 0 62.633-36.504 63.496-38.057a3.997 3.997 0 0 0 0-3.886zm-16.668-39.229-88 88C22.047 111.609 21.023 112 20 112s-2.047-.391-2.828-1.172a3.997 3.997 0 0 1 0-5.656l11.196-11.196C10.268 83.049 1.071 66.964.504 65.943a4 4 0 0 1 0-3.887C1.367 60.504 22.127 24 64 24c10.827 0 20.205 2.47 28.222 6.122l12.95-12.95c1.563-1.563 4.094-1.563 5.656 0s1.563 4.094 0 5.656zM34.333 88.011 44.46 77.884C41.663 73.96 40 69.175 40 64c0-13.234 10.766-24 24-24 5.175 0 9.96 1.663 13.884 4.459l8.189-8.189C79.603 33.679 72.251 32 64 32 32.045 32 13.447 56.775 8.707 63.994c3.01 4.562 11.662 16.11 25.626 24.017zm15.934-15.935 21.809-21.809C69.697 48.862 66.958 48 64 48c-8.822 0-16 7.178-16 16 0 2.958.862 5.697 2.267 8.076z"></path></g>
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="cha-auth-field">
                                <label htmlFor="cha-signup-confirm">{__( 'Confirm Password', 'changeloger' )}</label>
                                <div className="cha-auth-password-wrap">
                                    <input
                                        id="cha-signup-confirm"
                                        type={showConfirmPw ? 'text' : 'password'}
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        required
                                        placeholder={__( 'Confirm your password', 'changeloger' )}
                                    />
                                    <button
                                        type="button"
                                        className="cha-auth-eye-toggle"
                                        onClick={() => setShowConfirmPw(!showConfirmPw)}
                                        aria-label={showConfirmPw ? __( 'Hide password', 'changeloger' ) : __( 'Show password', 'changeloger' )}
                                    >
                                        {showConfirmPw ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 128 128" stroke="currentColor" width="18" height="18">
                                              <g transform="matrix(1,0,0,1,0,0)"><path opacity="1" fill='#475569' d="m79.891 65.078 7.27-7.27C87.69 59.787 88 61.856 88 64c0 13.234-10.766 24-24 24-2.144 0-4.213-.31-6.192-.839l7.27-7.27a15.929 15.929 0 0 0 14.813-14.813zm47.605-3.021c-.492-.885-7.47-13.112-21.11-23.474l-5.821 5.821c9.946 7.313 16.248 15.842 18.729 19.602C114.553 71.225 95.955 96 64 96c-4.792 0-9.248-.613-13.441-1.591l-6.573 6.573C50.029 102.835 56.671 104 64 104c41.873 0 62.633-36.504 63.496-38.057a3.997 3.997 0 0 0 0-3.886zm-16.668-39.229-88 88C22.047 111.609 21.023 112 20 112s-2.047-.391-2.828-1.172a3.997 3.997 0 0 1 0-5.656l11.196-11.196C10.268 83.049 1.071 66.964.504 65.943a4 4 0 0 1 0-3.887C1.367 60.504 22.127 24 64 24c10.827 0 20.205 2.47 28.222 6.122l12.95-12.95c1.563-1.563 4.094-1.563 5.656 0s1.563 4.094 0 5.656zM34.333 88.011 44.46 77.884C41.663 73.96 40 69.175 40 64c0-13.234 10.766-24 24-24 5.175 0 9.96 1.663 13.884 4.459l8.189-8.189C79.603 33.679 72.251 32 64 32 32.045 32 13.447 56.775 8.707 63.994c3.01 4.562 11.662 16.11 25.626 24.017zm15.934-15.935 21.809-21.809C69.697 48.862 66.958 48 64 48c-8.822 0-16 7.178-16 16 0 2.958.862 5.697 2.267 8.076z"></path></g>
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <button 
                                type="submit" 
                                className="cha-auth-submit"
                                disabled={loading}
                            >
                                {loading ? __( 'Creating account...', 'changeloger' ) : __( 'Sign Up', 'changeloger' )}
                            </button>
                        </form>
                    )}
                </div>

                {/* Footer */}
                <div className="cha-auth-footer">
                    {mode === 'login' ? (
                        <p>
                            {__( "Don't have an account?", 'changeloger' )}{' '}
                            {enableEmailSignup ? (
                                <button onClick={() => setMode('signup')}>{__( 'Sign up', 'changeloger' )}</button>
                            ) : (
                                <a href={registerUrl} target="_blank" rel="noopener">{__( 'Register', 'changeloger' )}</a>
                            )}
                        </p>
                    ) : (
                        <p>
                            {__( 'Already have an account?', 'changeloger' )}{' '}
                            <button onClick={() => setMode('login')}>{__( 'Log in', 'changeloger' )}</button>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
